/**
 * WebsiteTab — the study's public-website presence (the "Show on website"
 * control). Restores the WCR studies admin's publish capability inside the CRM:
 * flipping the toggle on makes the study live on westclinicalresearch.com — its
 * information page (/studies/<slug>) and its listing card — served from
 * crm.studies via the native serving endpoints.
 *
 * Two sections:
 *   1. Show on public website — the toggle plus the marketing/SEO copy the WCR
 *      info page + listing render (condition, descriptions, requirements bullets,
 *      compensation, age/duration/visits, featured-on-homepage, SEO meta).
 *   2. Ad landing page — designate the one published form the /study/<slug> ad
 *      page serves to paid traffic (decoupled from show_on_website).
 *
 * Admin-gated edits (mirrors OverviewTab); recruiters get a read-only view.
 * All fields PATCH /api/crm/studies/:id.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Globe, Loader2, Plus, X, ExternalLink, AlertTriangle, Sparkles, MessageSquare } from 'lucide-react';
import api from '../../../services/api';
import { CrmForm, Study, StudyDetailResponse } from '../../types';
import { studyHasExtractedDoc } from '../../lib';
import { latestArtifact } from './TextArtifactPanel';
import { Alert } from '../ui';

const WCR_BASE = (process.env.REACT_APP_WCR_PUBLIC_URL || 'https://westclinicalresearch.com').replace(/\/+$/, '');

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const labelCls = 'mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400';

/** The editable website fields (string fields default to '' for controlled inputs). */
interface WebsiteForm {
  show_on_website: boolean;
  is_featured: boolean;
  condition: string;
  short_description: string;
  full_description: string;
  requirements_summary: string[];
  compensation_text: string;
  age_range: string;
  study_duration: string;
  visit_count: string;
  meta_title: string;
  meta_description: string;
  landing_form_id: string; // '' = none
  /** Item #01: invite re-contact (Text/Call) on the ineligible result screen. */
  ineligible_cta_enabled: boolean;
}

function formFromStudy(study: Study): WebsiteForm {
  return {
    show_on_website: study.show_on_website === true,
    is_featured: study.is_featured === true,
    condition: study.condition || '',
    short_description: study.short_description || '',
    full_description: study.full_description || '',
    requirements_summary: Array.isArray(study.requirements_summary) ? study.requirements_summary : [],
    compensation_text: study.compensation_text || '',
    age_range: study.age_range || '',
    study_duration: study.study_duration || '',
    visit_count: study.visit_count || '',
    meta_title: study.meta_title || '',
    meta_description: study.meta_description || '',
    landing_form_id: study.landing_form_id || '',
    ineligible_cta_enabled: study.config?.ineligible_cta?.enabled === true,
  };
}

/** PATCH payload: trimmed strings -> null, requirements cleaned, landing_form_id -> null. */
function toPayload(f: WebsiteForm) {
  const s = (v: string) => (v.trim() ? v.trim() : null);
  return {
    show_on_website: f.show_on_website,
    is_featured: f.is_featured,
    condition: s(f.condition),
    short_description: s(f.short_description),
    full_description: s(f.full_description),
    requirements_summary: f.requirements_summary.map((r) => r.trim()).filter(Boolean),
    compensation_text: s(f.compensation_text),
    age_range: s(f.age_range),
    study_duration: s(f.study_duration),
    visit_count: s(f.visit_count),
    meta_title: s(f.meta_title),
    meta_description: s(f.meta_description),
    landing_form_id: f.landing_form_id || null,
    ineligible_cta_enabled: f.ineligible_cta_enabled,
  };
}

export default function WebsiteTab({ detail, isAdmin, onChanged }: { detail: StudyDetailResponse; isAdmin: boolean; onChanged: () => void }) {
  const { study } = detail;
  const [form, setForm] = useState<WebsiteForm>(() => formFromStudy(study));
  const [baseline, setBaseline] = useState(() => JSON.stringify(toPayload(formFromStudy(study))));
  const [forms, setForms] = useState<CrmForm[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [genBusy, setGenBusy] = useState(false);
  const [appliedArtifactId, setAppliedArtifactId] = useState<string | null>(null);

  // Re-sync local form when the study reloads (e.g. after another tab's save).
  useEffect(() => {
    const next = formFromStudy(study);
    setForm(next);
    setBaseline(JSON.stringify(toPayload(next)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [study.id, study.updated_at]);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/crm/forms', { params: { study_id: study.id } })
      .then((res) => { if (!cancelled) setForms(res.data?.forms || []); })
      .catch(() => { /* dropdown just stays empty; non-fatal */ });
    return () => { cancelled = true; };
  }, [study.id]);

  const dirty = useMemo(() => JSON.stringify(toPayload(form)) !== baseline, [form, baseline]);
  const set = <K extends keyof WebsiteForm>(key: K, value: WebsiteForm[K]) => setForm((f) => ({ ...f, [key]: value }));

  const publishableForms = forms.filter((f) => f.status === 'active' && (f.version || 0) > 0);
  const missingCopy = form.show_on_website && (!form.condition.trim() || !form.short_description.trim());

  // AI drafting: the latest website_content artifact (the protocol/ICF -> copy
  // generator, same pipeline as the summary/prescreen). hasExtractedDoc gates
  // the Generate button; the draft is applied INTO the form for review (Save
  // still persists — generating never auto-publishes).
  const hasExtractedDoc = studyHasExtractedDoc(detail);
  const wcArtifact = latestArtifact(detail.artifacts, 'website_content');
  const wcStatus = wcArtifact?.status;
  const wcGenerating = wcStatus === 'generating';
  const showApplyBanner = !!wcArtifact && wcStatus === 'draft' && appliedArtifactId !== wcArtifact.id;

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/api/crm/studies/${study.id}`, toPayload(form));
      // The saved fields are now the finalized copy — set aside any pending AI
      // draft so its "apply this instead" banner stops re-appearing on reload.
      // Non-fatal: a failed dismiss just leaves the banner (which is dismissable).
      if (wcArtifact && (wcStatus === 'draft' || wcStatus === 'failed')) {
        try { await api.post(`/api/crm/artifacts/${wcArtifact.id}/discard`, {}); } catch { /* non-fatal */ }
      }
      setBaseline(JSON.stringify(toPayload(form)));
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save website settings');
    } finally {
      setSaving(false);
    }
  }, [study.id, form, onChanged, wcArtifact, wcStatus]);

  // Kick off AI generation, then reload so the 'generating' artifact appears and
  // StudyDetail's poll picks up the flip to 'draft'.
  const generate = useCallback(async () => {
    setGenBusy(true);
    setError(null);
    try {
      await api.post(`/api/crm/studies/${study.id}/generate`, { types: ['website_content'] });
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to start generation');
    } finally {
      setGenBusy(false);
    }
  }, [study.id, onChanged]);

  // Load the AI draft into the editable form (marketing fields only — the toggle,
  // featured flag, and landing form stay under the user's control). Save persists.
  const applyDraft = useCallback(() => {
    const c = wcArtifact?.content;
    if (!c) return;
    setForm((f) => ({
      ...f,
      condition: c.condition || '',
      short_description: c.short_description || '',
      full_description: c.full_description || '',
      requirements_summary: Array.isArray(c.requirements_summary) ? c.requirements_summary : [],
      compensation_text: c.compensation_text || '',
      age_range: c.age_range || '',
      study_duration: c.study_duration || '',
      visit_count: c.visit_count || '',
      meta_title: c.meta_title || '',
      meta_description: c.meta_description || '',
    }));
    setAppliedArtifactId(wcArtifact!.id);
  }, [wcArtifact]);

  // Persistently set the AI draft aside (server-side) so the banner does NOT
  // return on the next visit — the session-only appliedArtifactId hides it now;
  // the discard makes it stick. Non-fatal if the discard fails.
  const dismissDraft = useCallback(async () => {
    if (!wcArtifact) return;
    setAppliedArtifactId(wcArtifact.id);
    if (wcStatus === 'draft' || wcStatus === 'failed') {
      try { await api.post(`/api/crm/artifacts/${wcArtifact.id}/discard`, {}); onChanged(); } catch { /* non-fatal */ }
    }
  }, [wcArtifact, wcStatus, onChanged]);

  const infoUrl = `${WCR_BASE}/studies/${study.slug}`;
  const adUrl = `${WCR_BASE}/study/${study.slug}`;

  // ---- Read-only view for recruiters ---------------------------------------
  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <Globe className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Public website
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {form.show_on_website
            ? <>This study is <span className="font-medium text-green-700 dark:text-green-400">live on the website</span>.</>
            : <>This study is <span className="font-medium text-gray-500">not shown on the website</span>.</>}
        </p>
        {form.show_on_website && (
          <a href={infoUrl} target="_blank" rel="noopener noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-800 dark:text-primary-400">
            <ExternalLink className="h-3.5 w-3.5" /> View public page
          </a>
        )}
      </div>
    );
  }

  // ---- Admin editor --------------------------------------------------------
  return (
    <div className="space-y-4">
      {/* Section 1: publish toggle + marketing copy */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
              <Globe className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Show on public website
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              When on, this study appears on the public studies listing and gets its own information page.
            </p>
          </div>
          {/* Toggle switch */}
          <button
            type="button"
            role="switch"
            aria-checked={form.show_on_website}
            onClick={() => set('show_on_website', !form.show_on_website)}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
              form.show_on_website ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-150 ${form.show_on_website ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* AI: draft the website copy from the study's protocol & ICF (same
            pipeline as the summary / prescreen generators). */}
        <div className="mb-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary-100 bg-primary-50/60 p-3 dark:border-primary-900/40 dark:bg-primary-900/10">
            <span className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
              <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
              Let AI draft this from the study&apos;s protocol &amp; ICF.
            </span>
            <button
              type="button"
              onClick={generate}
              disabled={genBusy || wcGenerating || !hasExtractedDoc}
              title={!hasExtractedDoc ? 'Upload a protocol/ICF in Documents and wait for extraction first' : undefined}
              className="inline-flex items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-300 dark:hover:bg-primary-900/20"
            >
              {wcGenerating
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Drafting…</>
                : genBusy
                  ? 'Starting…'
                  : <><Sparkles className="h-3.5 w-3.5" /> {wcArtifact ? 'Regenerate' : 'Generate'}</>}
            </button>
          </div>

          {wcGenerating && (
            <div className="flex items-center gap-2 rounded-lg bg-primary-50 p-3 text-sm text-primary-800 dark:bg-primary-900/20 dark:text-primary-200">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading your protocol &amp; ICF and drafting website content…
            </div>
          )}

          {wcStatus === 'failed' && (
            <Alert tone="error" icon={false}>
              Generation failed: {wcArtifact?.error || 'unknown error'}. Try again, or confirm the protocol/ICF finished extracting on the Documents tab.
            </Alert>
          )}

          {showApplyBanner && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
              <p className="text-xs font-medium text-amber-800 dark:text-amber-300">AI drafted website content from your documents.</p>
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                Apply it to the fields below, review every field, then Save. Applying never publishes the study on its own.
              </p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={applyDraft}
                  className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700"
                >
                  Apply to fields
                </button>
                <button
                  type="button"
                  onClick={dismissDraft}
                  title="Dismiss this AI draft so it doesn't come back — your saved copy stays as-is"
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>

        {missingCopy && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Add a <strong>condition</strong> and <strong>short description</strong> below — the public page and listing card look empty without them.</span>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Condition</label>
              <input type="text" placeholder="e.g. Type 2 Diabetes" value={form.condition} className={inputCls}
                onChange={(e) => set('condition', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Compensation (headline)</label>
              <input type="text" placeholder="e.g. Up to $1,050" value={form.compensation_text} className={inputCls}
                onChange={(e) => set('compensation_text', e.target.value)} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Short description <span className="text-gray-400">(listing card + meta fallback)</span></label>
            <textarea rows={2} placeholder="One or two sentences for the studies listing card." value={form.short_description} className={inputCls}
              onChange={(e) => set('short_description', e.target.value)} />
          </div>

          <div>
            <label className={labelCls}>Full description <span className="text-gray-400">(study information page body)</span></label>
            <textarea rows={6} placeholder="The detailed write-up shown on the study's information page." value={form.full_description} className={inputCls}
              onChange={(e) => set('full_description', e.target.value)} />
          </div>

          {/* Requirements bullets */}
          <div>
            <label className={labelCls}>“You may qualify if…” bullets</label>
            <div className="space-y-2">
              {form.requirements_summary.map((req, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={req}
                    placeholder="e.g. You are 18–75 years old"
                    className={inputCls}
                    onChange={(e) => set('requirements_summary', form.requirements_summary.map((r, j) => (j === i ? e.target.value : r)))}
                  />
                  <button
                    type="button"
                    onClick={() => set('requirements_summary', form.requirements_summary.filter((_, j) => j !== i))}
                    aria-label="Remove requirement"
                    className="shrink-0 rounded-lg border border-gray-300 p-2 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-red-600 dark:border-gray-600 dark:hover:bg-gray-700"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {form.requirements_summary.length < 30 && (
                <button
                  type="button"
                  onClick={() => set('requirements_summary', [...form.requirements_summary, ''])}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                >
                  <Plus className="h-3.5 w-3.5" /> Add requirement
                </button>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Age range</label>
              <input type="text" placeholder="e.g. 18–65" value={form.age_range} className={inputCls}
                onChange={(e) => set('age_range', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Study duration</label>
              <input type="text" placeholder="e.g. 12 weeks" value={form.study_duration} className={inputCls}
                onChange={(e) => set('study_duration', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Visit count</label>
              <input type="text" placeholder="e.g. 6 visits" value={form.visit_count} className={inputCls}
                onChange={(e) => set('visit_count', e.target.value)} />
            </div>
          </div>

          {/* Featured */}
          <label className="flex items-center gap-2.5">
            <input type="checkbox" checked={form.is_featured} onChange={(e) => set('is_featured', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
            <span className="text-sm text-gray-700 dark:text-gray-200">Feature on the homepage</span>
          </label>

          {/* SEO */}
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/30">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">SEO (optional)</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Meta title <span className="text-gray-400">(defaults to the study title)</span></label>
                <input type="text" value={form.meta_title} className={inputCls}
                  onChange={(e) => set('meta_title', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Meta description <span className="text-gray-400">(defaults to the short description)</span></label>
                <textarea rows={2} value={form.meta_description} className={inputCls}
                  onChange={(e) => set('meta_description', e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: ad landing page */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <h2 className="mb-1 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <Globe className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Ad landing page
        </h2>
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          The form the ad landing page (<code className="text-xs">/study/{study.slug}</code>) serves to paid traffic. Independent of the toggle above.
        </p>
        <label className={labelCls}>Landing form</label>
        <select value={form.landing_form_id} className={inputCls} onChange={(e) => set('landing_form_id', e.target.value)}>
          <option value="">None — ad landing page disabled</option>
          {publishableForms.map((f) => (
            <option key={f.id} value={f.id}>{f.title} (v{f.version})</option>
          ))}
        </select>
        {form.landing_form_id && !publishableForms.some((f) => f.id === form.landing_form_id) && (
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
            The designated form is not currently published — the ad landing page will 404 until it is. Publish it on the Forms tab.
          </p>
        )}
        {publishableForms.length === 0 && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            No published forms for this study yet. Build and publish one on the Forms tab first.
          </p>
        )}

        {/* Item #01: invite re-contact on the ineligible result screen. */}
        <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-700">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-prose">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <MessageSquare className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Invite re-contact on the “not eligible” screen
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                When on, leads who don&apos;t qualify see <strong>Text us now</strong> and <strong>Call us now</strong> buttons on the
                ad landing page&apos;s result screen, wired to the CRM&apos;s SMS/voice number. Leave off for studies that don&apos;t
                want to invite ineligible visitors to reach out.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.ineligible_cta_enabled}
              onClick={() => set('ineligible_cta_enabled', !form.ineligible_cta_enabled)}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                form.ineligible_cta_enabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-150 ${form.ineligible_cta_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Public links + sticky save */}
      <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-6">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Public links</p>
        <div className="flex flex-col gap-1.5 text-sm">
          <a href={infoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary-700 hover:text-primary-800 dark:text-primary-400">
            <ExternalLink className="h-3.5 w-3.5" /> Information page <span className="text-gray-400">{infoUrl}</span>
          </a>
          <a href={adUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-primary-700 hover:text-primary-800 dark:text-primary-400">
            <ExternalLink className="h-3.5 w-3.5" /> Ad landing page <span className="text-gray-400">{adUrl}</span>
          </a>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="sticky bottom-0 flex items-center justify-end gap-2 rounded-xl border border-gray-200 bg-white/90 p-3 backdrop-blur dark:border-gray-700 dark:bg-gray-800/90">
        {dirty && <span className="mr-auto text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>}
        <button
          type="button"
          onClick={() => { const next = formFromStudy(study); setForm(next); }}
          disabled={!dirty || saving}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : 'Save website settings'}
        </button>
      </div>
    </div>
  );
}
